import type { MovieDetail, TermKind, TermRef, VariantView } from '@zyx/shared';
import { ExternalLink, TriangleAlert } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ErrorState } from '@/components/common/states';
import { MovieBookmarks } from '@/components/movie/bookmarks';
import { DownloadList } from '@/components/movie/download-list';
import { MarkControls } from '@/components/movie/mark-controls';
import { MovieActions } from '@/components/movie/movie-actions';
import { PersonPhoto } from '@/components/movie/person-photo';
import { AssetImage } from '@/components/movie/asset';
import { nextNonce, type PlayRequest, Player, startingPoint } from '@/components/movie/player';
import { SceneStrip } from '@/components/movie/scene-strip';
import { Skeleton } from '@/components/ui/skeleton';
import { useDownloads } from '@/hooks/use-downloads';
import { useBookmarks } from '@/hooks/use-library';
import { useMovie } from '@/hooks/use-movies';
import { editionLabel, formatAgo, formatCount, formatRuntime } from '@/lib/format';
import { urls } from '@/lib/urls';
import { cn } from '@/lib/utils';

const TERM_ROWS: ReadonlyArray<readonly [TermKind, string]> = [
  ['maker', 'Maker'],
  ['label', 'Label'],
  ['series', 'Series'],
  ['genre', 'Genres'],
  ['tag', 'Tags'],
];

const EPISODE_PROBLEMS: Readonly<Record<string, string>> = {
  cdn_unresolvable: 'Its video host no longer exists',
  gone: 'The source no longer has this video',
  error: 'The video host refused it',
  unchecked: 'Not checked yet',
};

function Chip(props: { readonly to: string; readonly children: React.ReactNode; readonly withPhoto?: boolean }) {
  return (
    <Link
      to={props.to}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-border text-xs text-foreground/90 transition-theme hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        props.withPhoto === true ? 'py-0.5 pl-0.5 pr-2.5' : 'px-2.5 py-0.5',
      )}
    >
      {props.children}
    </Link>
  );
}

function Row(props: { readonly label: string; readonly children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[6.5rem_1fr] gap-3 text-sm">
      <dt className="text-muted-foreground">{props.label}</dt>
      <dd className="flex flex-wrap gap-1.5">{props.children}</dd>
    </div>
  );
}

function Edition(props: { readonly variant: VariantView; readonly primary: boolean; readonly onPlay: (request: PlayRequest) => void }) {
  const { variant } = props;
  const episodes = variant.episodes;
  const problems = episodes.filter((episode) => episode.status !== 'ok');
  return (
    <div className={cn('space-y-3 rounded-lg border p-4', props.primary ? 'border-primary/50' : 'border-border')}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-medium">{editionLabel(variant.edition)}</span>
        {props.primary ? <span className="rounded bg-primary/15 px-1.5 py-0.5 text-[11px] font-medium text-primary">Default</span> : null}
        {variant.gone === true ? <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">Gone from the source · kept offline</span> : null}
        <span className="text-sm text-muted-foreground">
          {[
            formatRuntime(variant.durationS),
            episodes.length > 1 ? `${episodes.length} parts` : undefined,
            formatAgo(variant.publishedAt),
            variant.sourceViews === undefined ? undefined : `${formatCount(variant.sourceViews)} views`,
          ]
            .filter((part) => part !== undefined)
            .join(' · ')}
        </span>
        <a href={variant.url} target="_blank" rel="noreferrer noopener" className="ml-auto inline-flex items-center gap-1 text-sm text-primary hover:underline">
          Open on {variant.sourceId}
          <ExternalLink className="size-3.5" />
        </a>
      </div>
      {problems.length === 0 ? null : (
        <ul className="space-y-1 text-sm text-muted-foreground">
          {problems.map((episode) => (
            <li key={episode.episodeId} className="flex items-center gap-1.5">
              <TriangleAlert className="size-3.5 text-amber-500" />
              {episodes.length > 1 ? `Part ${episode.episodeNo}: ` : ''}
              {EPISODE_PROBLEMS[episode.status] ?? episode.status}
            </li>
          ))}
        </ul>
      )}
      {episodes.map((episode) => (
        <div key={episode.episodeId} className="space-y-1.5">
          {episodes.length > 1 && episode.previewSprite !== undefined ? <p className="text-xs text-muted-foreground">Part {episode.episodeNo}</p> : null}
          <SceneStrip episode={episode} onSelect={(startS) => props.onPlay({ variantId: variant.variantId, episodeNo: episode.episodeNo, startS, nonce: nextNonce() })} />
        </div>
      ))}
    </div>
  );
}

/** A moment to open on, from `?variant=&part=&t=` (a bookmark's link), if this movie has that edition and part. */
function requestedStart(movie: MovieDetail, params: URLSearchParams): PlayRequest | undefined {
  const variantId = Number(params.get('variant'));
  const episodeNo = Number(params.get('part') ?? 1);
  const startS = Number(params.get('t') ?? 0);
  const variant = movie.variants.find((candidate) => candidate.variantId === variantId);
  if (variant === undefined || !variant.episodes.some((episode) => episode.episodeNo === episodeNo) || !Number.isFinite(startS) || startS < 0) {
    return undefined;
  }
  return { variantId, episodeNo, startS, nonce: nextNonce() };
}

function Detail(props: { readonly movie: MovieDetail }) {
  const { movie } = props;
  const termsOf = (kind: TermKind): ReadonlyArray<TermRef> => movie.terms.filter((term) => term.kind === kind);
  const ordered = [...movie.variants].sort((left, right) => Number(right.variantId === movie.primaryVariantId) - Number(left.variantId === movie.primaryVariantId));
  const [params, setParams] = useSearchParams();
  // A Play elsewhere (the home page's hero, a card) arrives as `?play=1`: start at once,
  // where the viewer left off. A bookmark's link names its moment instead.
  const [playing, setPlaying] = useState<PlayRequest | undefined>(() => {
    const start = requestedStart(movie, params);
    if (start !== undefined || params.get('play') !== '1') {
      return start;
    }
    const first = startingPoint(movie);
    return first === undefined ? undefined : { ...first, nonce: nextNonce() };
  });
  // Used once: a reload or Back must not start it again.
  useEffect(() => {
    if (params.has('play')) {
      const next = new URLSearchParams(params);
      next.delete('play');
      setParams(next, { replace: true });
    }
  }, [params, setParams]);
  const bookmarks = useBookmarks(movie.code);
  const downloads = useDownloads(movie.code);
  const offline = (downloads.data?.downloads ?? []).filter((download) => download.status === 'done');
  const canPlay = (variantId: number, episodeNo: number) => movie.variants.some((variant) => variant.variantId === variantId && variant.episodes.some((episode) => episode.episodeNo === episodeNo));
  const playerTop = useRef<HTMLDivElement>(null);
  const play = (request: PlayRequest) => {
    setPlaying(request);
    playerTop.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="relative isolate animate-rise space-y-10">
      {/* The movie's own artwork, blurred far back: the page takes its colour from it. */}
      <div className="pointer-events-none absolute -inset-x-4 -top-6 -z-10 h-[34rem] overflow-hidden sm:-inset-x-6 lg:-inset-x-8" aria-hidden>
        {movie.cover === undefined ? null : <AssetImage file={movie.cover} alt="" className="size-full scale-110 opacity-25 blur-3xl saturate-150" />}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/60 to-background" />
      </div>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] lg:gap-10">
        <div ref={playerTop} className="scroll-mt-20">
          <div className="overflow-hidden rounded-card bg-black/60 p-0 shadow-card-hover ring-1 ring-white/5">
            <Player movie={movie} offline={offline} request={playing} onRequest={play} />
          </div>
        </div>
        <div className="space-y-5">
          <div className="space-y-2">
            <p className="font-mono text-sm font-semibold text-primary">{movie.code}</p>
            <h1 className="font-display text-2xl leading-snug sm:text-3xl">{movie.title}</h1>
            <p className="text-sm text-muted-foreground">
              {[movie.releaseDate, formatRuntime(movie.durationS), movie.editions.map(editionLabel).join(' / ')].filter((part) => part !== undefined && part !== '').join(' · ')}
            </p>
          </div>
          <MarkControls code={movie.code} mark={movie.mark} />
          <MovieActions movie={movie} />
          <dl className="space-y-2.5">
            {movie.cast.length === 0 ? null : (
              <Row label="Cast">
                {movie.cast.map((person) => (
                  <Chip key={person.personId} to={urls.person(person.personId)} withPhoto>
                    <PersonPhoto name={person.name} photo={person.photo} className="size-6 text-[10px]" />
                    {person.name}
                  </Chip>
                ))}
              </Row>
            )}
            {TERM_ROWS.map(([kind, label]) =>
              termsOf(kind).length === 0 ? null : (
                <Row key={kind} label={label}>
                  {termsOf(kind).map((term) => (
                    <Chip key={term.termId} to={urls.term(term.termId)}>
                      {term.name}
                    </Chip>
                  ))}
                </Row>
              ),
            )}
          </dl>
          {movie.description === undefined ? null : <p className="whitespace-pre-line text-sm leading-relaxed text-foreground/85">{movie.description}</p>}
        </div>
      </div>
      {bookmarks.data === undefined || bookmarks.data.bookmarks.length === 0 ? null : (
        <section className="space-y-3">
          <h2 className="font-display text-xl sm:text-2xl">Bookmarks</h2>
          <MovieBookmarks
            bookmarks={bookmarks.data.bookmarks}
            playable={(bookmark) => canPlay(bookmark.variantId, bookmark.episodeNo)}
            detail={{
              parts: movie.variants.some((variant) => variant.episodes.length > 1),
              editions: new Set(bookmarks.data.bookmarks.map((bookmark) => bookmark.variantId)).size > 1 || movie.variants.length > 1,
            }}
            onPlay={(bookmark) => play({ variantId: bookmark.variantId, episodeNo: bookmark.episodeNo, startS: bookmark.positionS, nonce: nextNonce() })}
          />
        </section>
      )}
      {downloads.data === undefined || downloads.data.downloads.length === 0 ? null : (
        <section className="space-y-3">
          <h2 className="font-display text-xl sm:text-2xl">Downloads</h2>
          <DownloadList downloads={downloads.data.downloads} />
        </section>
      )}
      <section className="space-y-3">
        <h2 className="font-display text-xl sm:text-2xl">{movie.variants.length > 1 ? 'Editions' : 'Edition'}</h2>
        {ordered.map((variant) => (
          <Edition key={variant.variantId} variant={variant} primary={variant.variantId === movie.primaryVariantId} onPlay={play} />
        ))}
      </section>
    </div>
  );
}

export function MoviePage() {
  const { code = '' } = useParams();
  const query = useMovie(code);
  if (query.isPending) {
    return (
      <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
        <Skeleton className="aspect-[3/2] w-full rounded-xl" />
        <div className="space-y-3">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-2/3" />
        </div>
      </div>
    );
  }
  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  // Keyed by code, so moving to another movie starts its page afresh rather than keeping what played here.
  return <Detail key={query.data.movie.code} movie={query.data.movie} />;
}
